import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Field, ErrorMessage, Formik } from "formik";
import * as yup from "yup";
import axios from "axios";
import swal from "sweetalert";

import { FormActions, GetAQuoteFormStyled, SubmitButton } from "./styled";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const GET_A_QUOTE_FORM_SCRIPT = process.env.REACT_APP_GET_A_QUOTE_FORM_SCRIPT;

const REQUIRED_FIELD = "Required field";
const INVALID_EMAIL = "Invalid email";

const formSchema = yup.object().shape({
  name: yup.string().required(REQUIRED_FIELD),
  phone: yup
    .string()
    .min(10, "Must be at least 10 digits")
    .max(10, "Must be 10 digits maximum")
    .required(REQUIRED_FIELD),
  email: yup.string().email(INVALID_EMAIL).required(REQUIRED_FIELD),
  project: yup.string().required(REQUIRED_FIELD),
  recaptcha: yup.string().required("Solve recaptcha").nullable(),
});

const initialFormValues = {
  name: "",
  phone: "",
  email: "",
  project: "",
  recaptcha: "",
};

export const GetAQuoteForm = forwardRef(({ onSubmit }, forwardedRef) => {
  const navigate = useNavigate();
  const formikRef = useRef(null);
  const captchaRef = useRef(null);
  const captchaWidgetRef = useRef(null);

  useEffect(() => {
    const container = captchaRef.current;
    if (!container) return;

    let cancelled = false;
    let pollId = null;

    const renderWidget = () => {
      if (cancelled || !container.isConnected || container.innerHTML !== "") return;
      captchaWidgetRef.current = window.grecaptcha.render(container, {
        sitekey: RECAPTCHA_SITE_KEY,
        hl: "en",
        callback: (token) => {
          if (!cancelled) formikRef.current?.setFieldValue("recaptcha", token);
        },
        "expired-callback": () => {
          if (!cancelled) formikRef.current?.setFieldValue("recaptcha", "");
        },
      });
    };

    const tryInit = () => {
      if (window.grecaptcha?.render) {
        window.grecaptcha.ready(renderWidget);
        return;
      }
      // Script loading from another component (e.g. Contact page) — poll for it
      if (document.querySelector('script[src*="recaptcha/api.js"]')) {
        pollId = setInterval(() => {
          if (window.grecaptcha?.render) {
            clearInterval(pollId);
            window.grecaptcha.ready(renderWidget);
          }
        }, 100);
        return;
      }
      // No script loading at all — load it ourselves
      const callbackName = "__onRecaptchaLoadedGetAQuote";
      window[callbackName] = () => {
        delete window[callbackName];
        if (!cancelled) window.grecaptcha.ready(renderWidget);
      };
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?onload=${callbackName}&render=explicit`;
      script.async = true;
      document.head.appendChild(script);
    };

    tryInit();

    return () => {
      cancelled = true;
      clearInterval(pollId);
      if (captchaWidgetRef.current !== null && window.grecaptcha) {
        try { window.grecaptcha.reset(captchaWidgetRef.current); } catch (_) {}
        captchaWidgetRef.current = null;
      }
      if (container) container.innerHTML = "";
    };
  }, []);

  const cleanForm = () => {
    formikRef?.current?.resetForm();
    if (captchaWidgetRef.current !== null && window.grecaptcha) {
      try { window.grecaptcha.reset(captchaWidgetRef.current); } catch (_) {}
    }
  };

  useImperativeHandle(
    forwardedRef,
    () => ({
      cleanForm,
    }),
    []
  );

  const handleSubmit = async (values) => {
    const response = await axios.post(GET_A_QUOTE_FORM_SCRIPT, values);
    const responseData = response.data;

    if (responseData.success) {
      navigate("/ThankYou");
    }

    if (responseData.error) {
      swal("Error", "There was a problem sending the email", "error");
      cleanForm();
    }

    if (onSubmit) {
      onSubmit(responseData);
    }
  };

  return (
    <GetAQuoteFormStyled>
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <Field id="name" name="name" placeholder="Name*" />
            <ErrorMessage name="name" />
            <Field id="email" name="email" placeholder="Email*" />
            <ErrorMessage name="email" />
            <Field id="phone" name="phone" placeholder="Phone*" />
            <ErrorMessage name="phone" />
            <label htmlFor="project">Tell us about your project</label>
            <Field id="project" name="project" as="textarea" row="3"></Field>
            <ErrorMessage name="project" />
            <FormActions>
              <div>
                <div ref={captchaRef} />
                <ErrorMessage name="recaptcha" />
              </div>
              <div>
                <SubmitButton
                  type="submit"
                  weight
                  opacity="true"
                  font
                  size
                  absolute={false}
                  portada
                  disabled={isSubmitting}
                >
                  {isSubmitting && "Sending..."}
                  {!isSubmitting && "Submit"}
                </SubmitButton>
              </div>
            </FormActions>
          </Form>
        )}
      </Formik>
    </GetAQuoteFormStyled>
  );
});
